/**
 * `ExternalCvProfileView` — vista presentazionale completa e condivisa di un
 * profilo CV esterno, usata sia dalla pagina interna (`ExternalCvDetail`,
 * navigabile) sia dalla vista pubblica read-only (`PublicCvDashboard`).
 *
 * È puramente presentazionale: riceve già i dati aggregati (competenze, skill
 * valutate, posizionamento, certificazioni Credly) e il CV Europass estratto
 * (`parsedCv`), e li rende in un unico flusso verticale uniforme
 * (`.external-cv-detail`). Il comportamento navigazionale è controllato dalle
 * prop opzionali dei path di dettaglio: se assenti (vista pubblica) le card non
 * sono cliccabili e non compaiono link di drill-down.
 *
 * Le pagine ospitanti forniscono l'intestazione (`header`, tipicamente un
 * `PageHeader` con azioni specifiche) ed eventuali contenuti extra (`banner`,
 * es. alert): il resto del layout — dati personali, profilo professionale, riga
 * hero competenza + KPI, competenze, skill valutate, professioni e tutte le
 * sezioni del CV — vive qui, così le due pagine restano allineate.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  EXPERIENCE_RANGE_LABELS,
  type AssessmentView,
  type CompetencyMatch,
  type DashboardDto,
  type EuropassCertification,
  type EuropassCv,
  type ExternalCvAiStatus,
  type ExternalCvCertificationDto,
  type JobMatch,
  type JobPositioningDto,
  type ScoringConfigDto,
} from '../shared/dto';
import { CompetencyMatchCard } from './CompetencyMatchCard';
import {
  EuropassAwardsSection,
  EuropassEducationSection,
  EuropassLanguagesSection,
  EuropassPersonalInfoCard,
  EuropassProjectsSection,
  EuropassPublicationsSection,
  EuropassSkillsCard,
  EuropassSummaryCard,
  EuropassWorkExperienceSection,
  hasEuropassSkillsContent,
  hasPersonalInfoContent,
  hasSummaryContent,
  normalizeCertificationName,
} from './EuropassCvViewer';
import { SectionEditButton } from './EuropassCvSectionEditor';
import { JobMatchCard } from './JobMatchCard';
import {
  formatCertDate,
  certExpiryStatus,
  certIssuedTime,
  sortCertificationsByIssuedDesc,
  CERTIFICATION_PLACEHOLDER_IMAGE,
} from './CertificationCard';
import { LivelloBadge } from './LivelloBadge';
import { LevelSourcesModal } from './LevelSourcesModal';
import { levelSourcesCount, primaryLivello } from './skillLevelSources';
import { ScoreBadge } from './ScoreBadge';
import { ScoreCalcGuide } from './ScoreCalcGuide';
import { ScoreDetailModal } from './ScoreDetailModal';
import { ScoreGuide } from './ScoreGuide';
import { JobScoreGuide } from './JobScoreGuide';
import { LivelloGuide } from './LivelloGuide';
import { ABSENT_INDICATOR } from './ScoreTriple';
import {
  Badge,
  Card,
  DataTable,
  EmptyState,
  Icon,
  InfoHint,
  Loading,
  type Column,
} from './ui';

/**
 * Messaggi per lo stato dell'estrazione del CV Europass (`parsedCvStatus`),
 * indipendente dalla pre-compilazione skill: quando non è `completed` non c'è
 * un CV strutturato da mostrare, ma si informa comunque l'utente sullo stato.
 */
const PARSED_CV_STATUS_MESSAGE: Record<ExternalCvAiStatus, string> = {
  pending: 'Estrazione del CV in corso. Aggiorna tra poco per vedere il contenuto strutturato.',
  completed: '',
  failed:
    'Non è stato possibile estrarre il contenuto strutturato del CV. Puoi comunque scaricare il PDF originale.',
};

/**
 * Numero di competenze mostrate nella griglia sotto la hero (la competenza
 * principale). Totale in evidenza = 1 (hero) + `GRID_MATCHES_COUNT`.
 */
const GRID_MATCHES_COUNT = 3;

/** Numero di professioni principali mostrate in griglia (prima dell'accordion). */
const JOB_GRID_COUNT = 3;

/** Numero di skill (Score più alto) sempre visibili; le altre in accordion. */
const TOP_SKILLS_COUNT = 10;

/** Numero di certificazioni sempre visibili; le altre dietro l'accordion. */
const CERTIFICATIONS_GRID_COUNT = 3;

/** Ordina i match per `rankScore` decrescente, con `matchPercent` come tie-breaker. */
function sortByMatchDesc(matches: CompetencyMatch[]): CompetencyMatch[] {
  return [...matches].sort(
    (a, b) => b.rankScore - a.rankScore || b.matchPercent - a.matchPercent,
  );
}

/** Ordina i job per `rankScore` decrescente, con `matchPercent` come tie-breaker. */
function sortJobsByMatchDesc(jobs: JobMatch[]): JobMatch[] {
  return [...jobs].sort(
    (a, b) => b.rankScore - a.rankScore || b.matchPercent - a.matchPercent,
  );
}

/** `true` se la stringa è presente e non vuota dopo trim. */
function hasText(value: string | undefined): value is string {
  return value !== undefined && value.trim() !== '';
}

/**
 * Voce dell'elenco unificato delle certificazioni: una del catalogo/Credly
 * (`ExternalCvCertificationDto`) oppure una estratta dal CV
 * (`EuropassCertification`). Il discriminante `kind` seleziona la card.
 */
type CertificationEntry =
  | { kind: 'credly'; cert: ExternalCvCertificationDto }
  | { kind: 'cv'; cert: EuropassCertification };

/**
 * Data grezza di conseguimento di una voce: `issuedAt` (ISO) per le voci del
 * catalogo/Credly, `date` (testo libero) per quelle estratte dal CV.
 */
function certificationIssuedRaw(entry: CertificationEntry): string | null | undefined {
  return entry.kind === 'credly' ? entry.cert.issuedAt : entry.cert.date;
}

/**
 * Timestamp (ms) della data di conseguimento di una voce, per l'ordinamento
 * (più recente prima). Delega alla utility condivisa; `null` se la data è
 * assente o non interpretabile, così la voce finisce in coda.
 */
function certificationIssuedTime(entry: CertificationEntry): number | null {
  return certIssuedTime(certificationIssuedRaw(entry));
}

/** Nome di una certificazione (fallback vuoto), per l'ordinamento alfabetico. */
function certificationName(entry: CertificationEntry): string {
  return entry.cert.name?.trim() ?? '';
}

/** Riga normalizzata della tabella "Altre certificazioni". */
interface CertificationRow {
  key: string;
  name: string;
  issuer: string;
  /** Data di conseguimento già formattata (o `—` se assente). */
  issuedLabel: string;
  /** Timestamp (ms) di conseguimento per l'ordinamento, o `null` se assente. */
  issuedTime: number | null;
  /** Data di scadenza ISO (solo origine catalogo/Credly), o `null`. */
  expiresAt: string | null;
  /**
   * Id della certificazione per il link al dettaglio: presente solo per le voci
   * del catalogo/piattaforma (origine `credly`); assente per quelle estratte
   * dal CV, che non hanno una pagina di dettaglio.
   */
  certificationId?: string;
}

/**
 * Colonne della tabella "Altre certificazioni": nome, ente, data di
 * conseguimento (ordinabile, default decrescente) e scadenza con lo stesso
 * badge di stato della card.
 *
 * Se `certificationDetailPath` è fornito, il nome delle voci con
 * `certificationId` (origine catalogo/piattaforma) è un link al dettaglio; le
 * voci estratte dal CV restano testo semplice (nessun dettaglio).
 */
function buildCertificationColumns(
  certificationDetailPath?: (certificationId: string) => string,
  certificationBackTo?: string,
): Column<CertificationRow>[] {
  return [
    {
      key: 'name',
      header: 'Certificazione',
      render: (row) =>
        certificationDetailPath !== undefined && row.certificationId !== undefined ? (
          <Link
            to={certificationDetailPath(row.certificationId)}
            state={
              certificationBackTo !== undefined
                ? { from: certificationBackTo }
                : undefined
            }
            className="coverage-name__link"
            aria-label={`Apri il dettaglio di ${row.name}`}
          >
            {row.name}
            <Icon name="external" size={14} className="coverage-name__link-icon" />
          </Link>
        ) : (
          row.name
        ),
      sortable: true,
      sortAccessor: (row) => row.name.toLowerCase(),
    },
    {
      key: 'issuer',
      header: 'Ente',
      render: (row) => (row.issuer === '' ? '—' : row.issuer),
      sortable: true,
      sortAccessor: (row) => row.issuer.toLowerCase(),
    },
    {
      key: 'issuedAt',
      header: 'Conseguita',
      render: (row) => row.issuedLabel,
      sortable: true,
      // Ordina per timestamp; le voci senza data (`null`) restano sempre in coda
      // a prescindere dalla direzione.
      sortComparator: (a, b, direction) => {
        if (a.issuedTime === b.issuedTime) return 0;
        if (a.issuedTime === null) return 1;
        if (b.issuedTime === null) return -1;
        const diff = a.issuedTime - b.issuedTime;
        return direction === 'desc' ? -diff : diff;
      },
    },
    {
      key: 'expiresAt',
      header: 'Scadenza',
      render: (row) => {
        const status = certExpiryStatus(row.expiresAt);
        if (status === 'none') return 'Nessuna';
        if (status === 'expired') {
          return <Badge tone="danger">{formatCertDate(row.expiresAt)}</Badge>;
        }
        if (status === 'expiring') {
          return <Badge tone="warning">In scadenza {formatCertDate(row.expiresAt)}</Badge>;
        }
        return formatCertDate(row.expiresAt);
      },
    },
  ];
}

/**
 * Card di una certificazione importata da Credly per un profilo CV esterno.
 * Presentazionale, riusa le classi CSS di `certification-card` (come la card
 * degli utenti reali) ma opera su {@link ExternalCvCertificationDto}.
 */
function ExternalCvCertificationCard({
  certification: c,
}: {
  certification: ExternalCvCertificationDto;
}) {
  return (
    <Card>
      {c.badgeUrl !== undefined && (
        <a
          href={c.badgeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="certification-card__badge-link"
          aria-label={`Vedi il badge di ${c.name} su Credly`}
          title="Vedi badge"
        >
          <Icon name="external" />
        </a>
      )}

      <header className="certification-card__header">
        <img
          src={c.imageUrl ?? CERTIFICATION_PLACEHOLDER_IMAGE}
          alt=""
          aria-hidden="true"
          className="certification-card__image"
          width={64}
          height={64}
        />
        <div className="certification-card__heading">
          <h3 className="certification-card__name">{c.name}</h3>
        </div>
      </header>

      <hr className="certification-card__divider" />

      <dl className="certification-card__meta">
        {c.issuer !== undefined && (
          <div className="certification-card__meta-item certification-card__meta-item--issuer">
            <dt>Ente</dt>
            <dd>{c.issuer}</dd>
          </div>
        )}
        <div className="certification-card__meta-item">
          <dt>Conseguita</dt>
          <dd>{formatCertDate(c.issuedAt)}</dd>
        </div>
        <div className="certification-card__meta-item">
          <dt>Scadenza</dt>
          <dd>
            {(() => {
              const status = certExpiryStatus(c.expiresAt);
              if (status === 'none') return 'Nessuna';
              if (status === 'expired') {
                return <Badge tone="danger">{formatCertDate(c.expiresAt)}</Badge>;
              }
              if (status === 'expiring') {
                return (
                  <Badge tone="warning">In scadenza {formatCertDate(c.expiresAt)}</Badge>
                );
              }
              return formatCertDate(c.expiresAt);
            })()}
          </dd>
        </div>
      </dl>

      <hr className="certification-card__divider certification-card__divider--footer" />
      <footer className="certification-card__footer">
        <Badge tone="info">Credly</Badge>
        {/* Le skill collegate non si mostrano se la certificazione è scaduta
            (coerente con la card degli utenti reali: non è più un collegamento
            attivo). */}
        {certExpiryStatus(c.expiresAt) !== 'expired' &&
          c.skills.map((s) => (
            <Badge key={s.id} tone="neutral">
              {s.name}
            </Badge>
          ))}
      </footer>
    </Card>
  );
}

/**
 * Card di una certificazione estratta dal CV Europass ({@link EuropassCertification}),
 * resa con la stessa UI delle card Credly (`certification-card__*`) per unificare
 * la sezione "Certificazioni". A differenza delle Credly, una voce del CV non ha
 * immagine/badge, skill collegate né una rotta di dettaglio: mostra un
 * placeholder con l'iniziale, l'ente e la data così come compaiono nel CV
 * (testo libero, non normalizzato) e un badge "Da CV" nel footer.
 */
function CvCertificationCard({ certification: c }: { certification: EuropassCertification }) {
  const name = hasText(c.name) ? c.name : 'Certificazione';
  return (
    <Card>
      <header className="certification-card__header">
        <img
          src={CERTIFICATION_PLACEHOLDER_IMAGE}
          alt=""
          aria-hidden="true"
          className="certification-card__image"
          width={64}
          height={64}
        />
        <div className="certification-card__heading">
          <h3 className="certification-card__name">{name}</h3>
        </div>
      </header>

      <hr className="certification-card__divider" />

      <dl className="certification-card__meta">
        {hasText(c.issuer) && (
          <div className="certification-card__meta-item certification-card__meta-item--issuer">
            <dt>Ente</dt>
            <dd>{c.issuer}</dd>
          </div>
        )}
        {hasText(c.date) && (
          <div className="certification-card__meta-item">
            <dt>Conseguita</dt>
            <dd>{c.date}</dd>
          </div>
        )}
        {hasText(c.reference) && (
          <div className="certification-card__meta-item">
            <dt>Riferimento</dt>
            <dd>{c.reference}</dd>
          </div>
        )}
      </dl>

      <hr className="certification-card__divider certification-card__divider--footer" />
      <footer className="certification-card__footer">
        <Badge tone="neutral">Da CV</Badge>
      </footer>
    </Card>
  );
}

/**
 * Rende la fascia di anni di esperienza dichiarata, o "—" se assente. Per un
 * profilo esterno l'Experience_Range deriva dalla Cv_Evaluation.
 */
function experienceCell(row: AssessmentView) {
  if (row.yearsOfExperience === null) {
    return (
      <span className="skill-list__absent" aria-label="Esperienza: non dichiarata">
        {ABSENT_INDICATOR}
      </span>
    );
  }
  return (
    <span className="skill-list__years">
      {EXPERIENCE_RANGE_LABELS[row.yearsOfExperience]}
    </span>
  );
}

/**
 * Rende lo "Score" (Score_Skill derivato dal motore) come badge colorato, o "—"
 * quando assente. Per i profili esterni non si mostra il conteggio
 * certificazioni (non ne hanno per skill).
 */
function scoreCell(
  row: AssessmentView,
  onScoreClick?: (row: AssessmentView) => void,
) {
  if (row.score === null) {
    return (
      <span className="skill-list__absent" aria-label="Score: assente">
        {ABSENT_INDICATOR}
      </span>
    );
  }
  // Con un handler (vista interna autenticata) lo Score è cliccabile e apre il
  // dettaglio del calcolo; senza (vista pubblica) resta un badge statico.
  if (onScoreClick === undefined) {
    return (
      <span className="skill-list__score-cell">
        <ScoreBadge value={row.score} label="Score" />
      </span>
    );
  }
  return (
    <span className="skill-list__score-cell">
      <button
        type="button"
        className="score-badge-button"
        onClick={() => onScoreClick(row)}
        aria-label={`Score ${row.score.toFixed(1)} su 10 — mostra il dettaglio del calcolo`}
        title="Mostra come è calcolato lo Score"
      >
        <ScoreBadge value={row.score} label="Score" />
      </button>
    </span>
  );
}

/**
 * Rende la colonna "Livello": il badge con il Livello scelto da `primaryLivello`
 * (Self preferito, poi revisioni concordi, poi AI) e — quando le Fonti di
 * Livello sono più d'una e `onShowSources` è fornito — un contatore cliccabile
 * che apre lo schemino con tutti i Livelli per fonte ({@link LevelSourcesModal}).
 * Replica il comportamento di `levelSelfCell` nella tabella interna.
 */
function levelCell(row: AssessmentView, onShowSources?: (row: AssessmentView) => void) {
  const sources = levelSourcesCount(row);
  const showCounter = sources > 1 && onShowSources !== undefined;
  const counterLabel = `${sources} fonti di Livello — mostra il dettaglio`;
  return (
    <span className="skill-list__level-cell">
      <LivelloBadge livello={primaryLivello(row)} />
      {showCounter && (
        <button
          type="button"
          className="skill-list__level-sources-count"
          onClick={() => onShowSources(row)}
          title={counterLabel}
          aria-label={counterLabel}
        >
          {sources}
        </button>
      )}
    </span>
  );
}

/**
 * Colonne della tabella skill valutate: Skill, Livello, Esperienza, Score. Le
 * colonne Self/Reviews/Propensione sono omesse.
 *
 * La colonna "Livello" mostra **sempre** un Livello quando almeno una fonte lo
 * dichiara: si preferisce il Livello auto‑dichiarato (Self), con fallback alle
 * revisioni concordi e all'AI (vedi `primaryLivello`). Quando le Fonti di
 * Livello sono più d'una, accanto al badge compare un contatore cliccabile che
 * apre lo schemino con tutti i Livelli per fonte ({@link LevelSourcesModal}),
 * come nella tabella interna. In un profilo CV esterno l'AI è tipicamente
 * l'unica fonte: il contatore non compare e il badge mostra il Livello AI, come
 * prima.
 */
function buildSkillColumns(
  descriptionBySkillId: Map<string, string | undefined>,
  onScoreClick?: (row: AssessmentView) => void,
  onShowLevelSources?: (row: AssessmentView) => void,
): Column<AssessmentView>[] {
  return [
    {
      key: 'skillName',
      header: 'Skill',
      sortable: true,
      sortAccessor: (row) => row.skillName.toLowerCase(),
      render: (row) => (
        <span className="skill-list__skill-cell">
          <span className="skill-list__skill-name">{row.skillName}</span>
          <InfoHint
            text={descriptionBySkillId.get(row.skillId)}
            label={`Descrizione di ${row.skillName}`}
          />
        </span>
      ),
    },
    {
      key: 'aiLevel',
      header: (
        <span className="score-calc-guide__header">
          Livello
          <LivelloGuide className="score-calc-guide__trigger" />
        </span>
      ),
      render: (row) => levelCell(row, onShowLevelSources),
      className: 'skill-list__level-col',
    },
    {
      key: 'experience',
      header: 'Esperienza',
      render: (row) => experienceCell(row),
      className: 'skill-list__experience-col',
    },
    {
      key: 'score',
      header: (
        <span className="score-calc-guide__header">
          Score
          <ScoreCalcGuide />
        </span>
      ),
      render: (row) => scoreCell(row, onScoreClick),
      className: 'skill-list__score-col',
    },
  ];
}

/** Props della vista condivisa del profilo CV esterno. */
export interface ExternalCvProfileViewProps {
  /** Intestazione della pagina (tipicamente un `PageHeader` con azioni proprie). */
  header: ReactNode;
  /** Contenuti mostrati tra l'header e le sezioni (es. alert di errore). */
  banner?: ReactNode;
  dashboard: DashboardDto;
  assessments: AssessmentView[];
  jobPositioning: JobPositioningDto;
  /** Certificazioni importate da Credly, associate al profilo. */
  certifications: ExternalCvCertificationDto[];
  /** CV Europass estratto, o `null` se non ancora disponibile. */
  parsedCv: EuropassCv | null;
  /** Esito dell'estrazione Europass. */
  parsedCvStatus: ExternalCvAiStatus;
  /** Stato di caricamento del CV (query dedicata, solo flusso interno). */
  cvLoading?: boolean;
  /** Contenuto di errore del CV (solo flusso interno); sostituisce le sezioni CV. */
  cvError?: ReactNode;
  /**
   * Descrizioni skill per l'hint accanto al nome nella tabella. Assente nella
   * vista pubblica (nessun catalogo): la mappa resta vuota.
   */
  descriptionBySkillId?: Map<string, string | undefined>;
  /**
   * Configurazione di scoring (pesi fonti/assi, `minScore`). Se presente, lo
   * Score di ogni skill diventa **cliccabile** e apre il dettaglio del calcolo
   * ({@link ScoreDetailModal}), così da capire il peso delle review. Passata
   * solo dal flusso interno autenticato (`ExternalCvDetail`); nella vista
   * pubblica resta assente e lo Score non è cliccabile.
   */
  scoringConfig?: ScoringConfigDto;
  /**
   * Interruttore generale di navigabilità delle card competenza/professione. Con
   * `false` (vista pubblica storica) sono tutte in sola lettura. Con `true`
   * (default) ciascun tipo di card è navigabile **solo se** è fornito anche il
   * rispettivo path (`competenceDetailPath`/`jobDetailPath`): così si può
   * abilitare il drill-down di un singolo tipo (es. solo competenza nel profilo
   * pubblico) senza rendere cliccabili le altre card verso rotte inesistenti.
   */
  interactive?: boolean;
  /** Path di dettaglio competenza (assente ⇒ card competenza non navigabile). */
  competenceDetailPath?: (competenceId: string) => string;
  /** Path di dettaglio professione (assente ⇒ card professione non navigabile). */
  jobDetailPath?: (jobId: string) => string;
  /**
   * Percorso di provenienza passato come `state.from` sulla navigazione alle
   * card professione, così la pagina di dettaglio può tornare al punto giusto
   * (es. `/assessment` quando si arriva da "Il mio CV") invece che a "Le mie
   * professioni". Assente ⇒ nessuno stato di navigazione (il dettaglio usa il
   * proprio default).
   */
  jobBackTo?: string;
  /**
   * Path di dettaglio certificazione (assente ⇒ nessuno "stretched link"): le
   * card Credly diventano cliccabili solo se fornito (flusso interno).
   */
  certificationDetailPath?: (certificationId: string) => string;
  /**
   * Percorso di provenienza passato come `state.from` sui link alle
   * certificazioni, così la pagina di dettaglio può tornare indietro al punto
   * giusto (es. `/assessment` quando si arriva da "Il mio CV"). Assente ⇒
   * nessuno stato di navigazione (il dettaglio usa il proprio default).
   */
  certificationBackTo?: string;
  /**
   * URL del wizard di autovalutazione. Se presente, nel KPI "Skill valutate"
   * compare la CTA "Valuta" (con effetto shine), come nella dashboard
   * dell'utente loggato. Usato dal flusso pubblico per far valutare al candidato
   * le proprie skill. Assente ⇒ nessuna CTA (vista interna read-only).
   */
  evaluateHref?: string;
  /**
   * Se `true`, nella colonna KPI compare la card "Recensioni ricevute"
   * (valorizzata da `dashboard.reviewsReceived`), come nella dashboard
   * dell'utente loggato. Usato dal flusso "Il mio CV". Assente/`false` ⇒ la
   * card non viene renderizzata (flussi CV esterni/pubblico, dove le recensioni
   * ricevute non sono significative).
   */
  showReviewsReceived?: boolean;
  /**
   * Se `true`, le sezioni del CV sono editabili (controlli di modifica,
   * aggiunta/rimozione item). Default `false` (read-only, flusso esterni):
   * nessun controllo di modifica viene renderizzato e il comportamento è
   * identico alla vista attuale.
   */
  editable?: boolean;
  /** Callback invocata quando l'utente salva una modifica a una sezione. */
  onSectionUpdate?: (section: keyof EuropassCv, data: unknown) => Promise<void>;
  /** Callback invocata quando l'utente aggiunge un item a una sezione ripetibile. */
  onItemAdd?: (section: string, item: unknown) => Promise<void>;
  /** Callback invocata quando l'utente rimuove un item da una sezione ripetibile. */
  onItemRemove?: (section: string, index: number) => Promise<void>;
}

/** Prefisso stabile per gli id degli elementi (evita collisioni tra istanze). */
const IDS = {
  matches: 'external-cv-matches-title',
  skills: 'external-cv-skills-title',
  jobs: 'external-cv-jobs-title',
  certifications: 'external-cv-certifications-title',
} as const;

export function ExternalCvProfileView({
  header,
  banner,
  dashboard,
  assessments,
  jobPositioning,
  certifications,
  parsedCv,
  parsedCvStatus,
  cvLoading = false,
  cvError,
  descriptionBySkillId,
  scoringConfig,
  interactive = true,
  competenceDetailPath,
  jobDetailPath,
  jobBackTo,
  certificationDetailPath,
  certificationBackTo,
  evaluateHref,
  showReviewsReceived,
  // Editable_Mode: con `editable` falso/assente non si passa nulla ai
  // sotto-componenti (vedi `editableSectionProps`), così nessun controllo di
  // modifica viene renderizzato e il comportamento resta identico alla vista
  // read-only del flusso CV esterni.
  editable,
  onSectionUpdate,
  onItemAdd,
  onItemRemove,
}: ExternalCvProfileViewProps) {
  // Riga di cui è aperto il dettaglio di calcolo dello Score (o `null`).
  // Valorizzata solo nel flusso interno (quando `scoringConfig` è presente): il
  // modale è puramente informativo.
  const [scoreDetailRow, setScoreDetailRow] = useState<AssessmentView | null>(null);
  // Riga di cui è aperto lo schemino con tutti i Livelli per fonte (o `null`).
  // Indipendente da `scoringConfig`: lo schemino è disponibile ogni volta che la
  // skill ha più di una Fonte di Livello.
  const [levelSourcesRow, setLevelSourcesRow] = useState<AssessmentView | null>(null);
  // Le competenze oltre le prime 4 (hero + 3 in griglia) sono nascoste finché
  // non si espande.
  const [showAll, setShowAll] = useState(false);
  // Le professioni oltre le prime 3 (con match > 0%) sono dietro un accordion.
  const [showAllJobs, setShowAllJobs] = useState(false);
  // Le skill oltre le prime TOP_SKILLS_COUNT sono nascoste finché non si espande.
  const [showAllSkills, setShowAllSkills] = useState(false);
  // Le certificazioni oltre le prime CERTIFICATIONS_GRID_COUNT sono dietro un accordion.
  const [showAllCertifications, setShowAllCertifications] = useState(false);
  // Apertura del modale di modifica delle competenze testuali del CV. Il trigger
  // "Modifica" vive sul titolo della sezione "Competenze"; il modale è reso
  // dalla card `EuropassSkillsCard` (controllata da questo stato).
  const [skillsEditOpen, setSkillsEditOpen] = useState(false);

  const descriptions = useMemo(
    () => descriptionBySkillId ?? new Map<string, string | undefined>(),
    [descriptionBySkillId],
  );
  const skillColumns = useMemo(
    () =>
      buildSkillColumns(
        descriptions,
        scoringConfig !== undefined ? (row) => setScoreDetailRow(row) : undefined,
        (row) => setLevelSourcesRow(row),
      ),
    [descriptions, scoringConfig],
  );

  // Navigabilità per tipo di card: richiede sia l'interruttore generale
  // (`interactive`) sia la presenza del path di dettaglio corrispondente. In
  // questo modo un tipo di card diventa cliccabile solo se ha una destinazione
  // valida, evitando fallback verso rotte di un contesto diverso (es. la vista
  // pubblica abilita solo la competenza, senza `jobDetailPath`).
  const competenceInteractive = interactive && competenceDetailPath !== undefined;
  const jobInteractive = interactive && jobDetailPath !== undefined;

  const certificationColumns = useMemo(
    () => buildCertificationColumns(certificationDetailPath, certificationBackTo),
    [certificationDetailPath, certificationBackTo],
  );

  const orderedMatches = useMemo(
    () => sortByMatchDesc(dashboard.competencyMatches ?? []),
    [dashboard],
  );
  const rankedJobs = useMemo(
    () =>
      sortJobsByMatchDesc(
        (jobPositioning.jobMatches ?? []).filter((j) => j.matchPercent > 0),
      ),
    [jobPositioning],
  );
  const orderedSkillRows = useMemo(
    () =>
      [...assessments].sort((a, b) => {
        const va = a.score;
        const vb = b.score;
        if (va === null && vb === null) {
          return a.skillName.localeCompare(b.skillName, 'it', { sensitivity: 'base' });
        }
        if (va === null) return 1;
        if (vb === null) return -1;
        if (vb !== va) return vb - va;
        return a.skillName.localeCompare(b.skillName, 'it', { sensitivity: 'base' });
      }),
    [assessments],
  );

  // Certificazioni Credly (per merge con quelle del CV per nome normalizzato).
  const credlyCertificationNames = useMemo(
    () => new Set(certifications.map((c) => normalizeCertificationName(c.name))),
    [certifications],
  );
  // Certificazioni del CV senza corrispondenza Credly: nella sezione unificata.
  const cvOnlyCertifications = useMemo(() => {
    return (parsedCv?.certifications ?? []).filter((c) => {
      const name = c.name?.trim() ?? '';
      const hasIssuer = c.issuer !== undefined && c.issuer.trim() !== '';
      if (name === '' && !hasIssuer) return false;
      if (name !== '' && credlyCertificationNames.has(normalizeCertificationName(name))) {
        return false;
      }
      return true;
    });
  }, [parsedCv, credlyCertificationNames]);

  const [heroMatch, ...restMatches] = orderedMatches;
  const gridMatches = restMatches.slice(0, GRID_MATCHES_COUNT);
  const otherMatches = restMatches.slice(GRID_MATCHES_COUNT);
  const hasOtherMatches = otherMatches.length > 0;

  const topJobs = rankedJobs.slice(0, JOB_GRID_COUNT);
  const otherJobs = rankedJobs.slice(JOB_GRID_COUNT);
  const hasOtherJobs = otherJobs.length > 0;

  const topSkillRows = orderedSkillRows.slice(0, TOP_SKILLS_COUNT);
  const otherSkillRows = orderedSkillRows.slice(TOP_SKILLS_COUNT);
  const hasOtherSkills = otherSkillRows.length > 0;

  const hasCvSkills = parsedCv !== null && hasEuropassSkillsContent(parsedCv);
  const totalCertifications = certifications.length + cvOnlyCertifications.length;

  // Elenco unificato delle certificazioni (catalogo/Credly + estratte dal CV),
  // con un discriminante di tipo per scegliere la card corretta, ordinato per
  // data di conseguimento decrescente (le più recenti prima). Serve a splittare
  // l'elenco "prime N + accordion" come nelle altre sezioni, dando priorità
  // alle ultime conseguite. Le voci senza una data valida vanno in fondo, con
  // ordinamento alfabetico stabile per nome.
  const certificationEntries = useMemo(() => {
    const entries: CertificationEntry[] = [
      ...certifications.map((c) => ({ kind: 'credly' as const, cert: c })),
      ...cvOnlyCertifications.map((c) => ({ kind: 'cv' as const, cert: c })),
    ];
    return sortCertificationsByIssuedDesc(
      entries,
      certificationIssuedRaw,
      certificationName,
    );
  }, [certifications, cvOnlyCertifications]);
  const topCertifications = certificationEntries.slice(0, CERTIFICATIONS_GRID_COUNT);
  const otherCertifications = certificationEntries.slice(CERTIFICATIONS_GRID_COUNT);
  const hasOtherCertifications = otherCertifications.length > 0;

  // Righe della tabella "Altre certificazioni": una forma normalizzata comune a
  // entrambe le origini (catalogo/Credly e CV). `issuedTime` è il timestamp per
  // l'ordinamento della colonna "Conseguita" (null in coda).
  const otherCertificationRows = useMemo<CertificationRow[]>(
    () =>
      otherCertifications.map((entry, index) => {
        const name = certificationName(entry) || 'Certificazione';
        const issuer = entry.cert.issuer?.trim() ?? '';
        const issuedTime = certificationIssuedTime(entry);
        const issuedAt =
          entry.kind === 'credly' ? formatCertDate(entry.cert.issuedAt) : entry.cert.date?.trim() ?? '';
        const expiresAt = entry.kind === 'credly' ? entry.cert.expiresAt : null;
        return {
          key: `other-cert-${index}-${name}`,
          name,
          issuer,
          issuedLabel: issuedAt === '' ? '—' : issuedAt,
          issuedTime,
          expiresAt,
          certificationId:
            entry.kind === 'credly' ? entry.cert.certificationId : undefined,
        };
      }),
    [otherCertifications],
  );

  // Prop dell'Editable_Mode propagate alle sezioni ripetibili del viewer. Con
  // `editable` falso/assente l'oggetto porta `editable` non-true e i
  // sotto-componenti non rendono alcun controllo (comportamento read-only
  // identico al flusso CV esterni).
  const editableSectionProps = {
    editable,
    onSectionUpdate,
    onItemAdd,
    onItemRemove,
  };

  // In Editable_Mode le sezioni devono essere SEMPRE compilabili, anche senza un
  // CV: si usa un oggetto vuoto (`{}`) quando `parsedCv` è `null`, così le
  // sezioni (ripetibili e singole) si montano e mostrano i controlli di
  // aggiunta/modifica. In sola lettura resta `null` (comportamento invariato).
  const cvForSections: EuropassCv | null =
    parsedCv ?? (editable === true ? {} : null);

  // Renderizza una singola voce dell'elenco unificato delle certificazioni,
  // scegliendo la card in base all'origine: catalogo/Credly (con eventuale link
  // di dettaglio) oppure estratta dal CV. L'`index` è quello globale (continuo
  // tra lista visibile e accordion) per chiavi stabili.
  const renderCertificationEntry = (
    entry: (typeof certificationEntries)[number],
    index: number,
  ) => {
    if (entry.kind === 'credly') {
      const c = entry.cert;
      return (
        <li
          key={c.certificationId}
          className={
            certificationDetailPath
              ? 'certification-card certification-card--linked'
              : 'certification-card'
          }
        >
          {certificationDetailPath && (
            <Link
              to={certificationDetailPath(c.certificationId)}
              state={
                certificationBackTo !== undefined
                  ? { from: certificationBackTo }
                  : undefined
              }
              className="certification-card__stretched-link"
              aria-label={`Apri il dettaglio di ${c.name}`}
            />
          )}
          <ExternalCvCertificationCard certification={c} />
        </li>
      );
    }
    return (
      <li key={`cv-cert-${entry.cert.name ?? ''}-${index}`} className="certification-card">
        <CvCertificationCard certification={entry.cert} />
      </li>
    );
  };

  return (
    <section aria-labelledby="external-cv-detail-title" className="external-cv-detail">
      {header}
      {banner}

      {/* Hero card dei dati personali e, subito sotto, il box del profilo
          professionale. In Editable_Mode sono sempre montate (anche vuote) con i
          controlli di modifica; in sola lettura compaiono solo se valorizzate. */}
      {cvForSections !== null &&
        (editable === true || hasPersonalInfoContent(cvForSections)) && (
          <EuropassPersonalInfoCard cv={cvForSections} hero {...editableSectionProps} />
        )}
      {cvForSections !== null &&
        (editable === true || hasSummaryContent(cvForSections)) && (
          <EuropassSummaryCard cv={cvForSections} {...editableSectionProps} />
        )}

      {/* Riga superiore: hero (competenza principale) a sinistra, KPI a destra. */}
      <div className="dashboard__top">
        <div className="dashboard__hero">
          {heroMatch ? (
            <CompetencyMatchCard
              match={heroMatch}
              hero
              interactive={competenceInteractive}
              detailPath={competenceDetailPath}
            />
          ) : (
            <EmptyState role="status">
              Nessun match disponibile per questo profilo.
            </EmptyState>
          )}
        </div>
        <div className="dashboard__kpis">
          <article className="kpi-card" aria-label="Skill valutate">
            <span className="kpi-card__label">Skill valutate</span>
            {evaluateHref !== undefined ? (
              // Con `evaluateHref` (flusso pubblico): valore + CTA "Valuta" con
              // effetto shine, come nella dashboard dell'utente loggato.
              <div className="kpi-card__value-row">
                <span className="kpi-card__value">{dashboard.evaluatedSkillCount}</span>
                <Link
                  to={evaluateHref}
                  className="kpi-card__cta kpi-card__cta--shine"
                >
                  Valuta
                </Link>
              </div>
            ) : (
              <span className="kpi-card__value">{dashboard.evaluatedSkillCount}</span>
            )}
          </article>
          <article className="kpi-card" aria-label="Certificazioni ottenute">
            <span className="kpi-card__label">Certificazioni</span>
            <span className="kpi-card__value">{totalCertifications}</span>
          </article>
          {showReviewsReceived === true && (
            <article className="kpi-card" aria-label="Recensioni ricevute">
              <span className="kpi-card__label">Recensioni ricevute</span>
              {/* Totale valutatori distinti; in empty state mostra `0`. */}
              <span className="kpi-card__value">{dashboard.reviewsReceived.total}</span>
              {/* Ripartizione per ruolo, mostrata solo se c'è almeno una recensione. */}
              {dashboard.reviewsReceived.total > 0 && (
                <span className="kpi-card__hint">
                  {dashboard.reviewsReceived.reviewerCount} reviewer ·{' '}
                  {dashboard.reviewsReceived.managerCount} manager
                </span>
              )}
            </article>
          )}
        </div>
      </div>

      {/* Competenze: card competenze testuali del CV subito sotto il titolo,
          poi griglia/accordion delle competenze del catalogo (DB). */}
      {(gridMatches.length > 0 || hasOtherMatches || hasCvSkills || editable === true) && (
        <section aria-labelledby={IDS.matches} className="europass-cv__section">
          <div className="dashboard__matches-heading">
            <h2 id={IDS.matches}>Competenze</h2>
            <ScoreGuide />
            {/* In Editable_Mode il pulsante "Modifica" delle competenze testuali
                del CV vive qui, a destra del titolo della sezione; il modale è
                reso dalla card sottostante (controllata da `skillsEditOpen`). */}
            {editable === true && (
              <div className="europass-cv__section-heading-action">
                <SectionEditButton
                  label="Modifica"
                  onClick={() => setSkillsEditOpen(true)}
                />
              </div>
            )}
          </div>

          {cvForSections !== null && (
            <EuropassSkillsCard
              cv={cvForSections}
              editOpen={skillsEditOpen}
              onEditClose={() => setSkillsEditOpen(false)}
              {...editableSectionProps}
            />
          )}

          {gridMatches.length > 0 && (
            <ul className="dashboard__matches">
              {gridMatches.map((match) => (
                <li key={match.competenceId}>
                  <CompetencyMatchCard
                    match={match}
                    interactive={competenceInteractive}
                    detailPath={competenceDetailPath}
                  />
                </li>
              ))}
            </ul>
          )}

          {hasOtherMatches && (
            <>
              <button
                type="button"
                className="dashboard__matches-accordion"
                aria-expanded={showAll}
                aria-controls="external-cv-other-matches"
                onClick={() => setShowAll((prev) => !prev)}
              >
                <span className="dashboard__matches-accordion-label">
                  Altre competenze ({otherMatches.length})
                </span>
                <span className="dashboard__matches-accordion-icon" aria-hidden="true" />
              </button>

              {showAll && (
                <ul
                  id="external-cv-other-matches"
                  className="dashboard__matches-list"
                  aria-label="Altre competenze"
                >
                  {otherMatches.map((match) => (
                    <li key={match.competenceId}>
                      <CompetencyMatchCard
                        match={match}
                        interactive={competenceInteractive}
                        detailPath={competenceDetailPath}
                        compact
                      />
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      )}

      {/* Tabella delle skill valutate: Skill, Livello, Esperienza, Score. */}
      <section aria-labelledby={IDS.skills} className="europass-cv__section">
        <h2 id={IDS.skills} className="europass-cv__section-title">
          Skill valutate
        </h2>
        <DataTable
          rows={topSkillRows}
          columns={skillColumns}
          rowKey={(row) => row.skillId}
          emptyMessage="Nessuna skill valutata per questo profilo."
          defaultSort={{ key: 'score', direction: 'desc' }}
          paginated={false}
        />

        {hasOtherSkills && (
          <>
            <button
              type="button"
              className="dashboard__matches-accordion"
              aria-expanded={showAllSkills}
              aria-controls="external-cv-other-skills"
              onClick={() => setShowAllSkills((prev) => !prev)}
            >
              <span className="dashboard__matches-accordion-label">
                Altre skill ({otherSkillRows.length})
              </span>
              <span className="dashboard__matches-accordion-icon" aria-hidden="true" />
            </button>

            {showAllSkills && (
              <div
                id="external-cv-other-skills"
                className="team-member-dashboard__other-skills"
              >
                <DataTable
                  rows={otherSkillRows}
                  columns={skillColumns}
                  rowKey={(row) => row.skillId}
                  emptyMessage="Nessuna altra skill."
                  defaultSort={{ key: 'score', direction: 'desc' }}
                  paginated={false}
                />
              </div>
            )}
          </>
        )}
      </section>

      {/* Professioni: quelle con match > 0%, ordinate per rankScore desc. */}
      {topJobs.length > 0 && (
        <section aria-labelledby={IDS.jobs} className="europass-cv__section">
          <div className="dashboard__matches-heading">
            <h2 id={IDS.jobs}>Professioni</h2>
            <JobScoreGuide />
          </div>
          <ul className="dashboard__matches">
            {topJobs.map((job) => (
              <li key={job.jobId}>
                <JobMatchCard
                  job={job}
                  interactive={jobInteractive}
                  detailPath={jobDetailPath}
                  backTo={jobBackTo}
                />
              </li>
            ))}
          </ul>

          {hasOtherJobs && (
            <>
              <button
                type="button"
                className="dashboard__matches-accordion"
                aria-expanded={showAllJobs}
                aria-controls="external-cv-other-jobs"
                onClick={() => setShowAllJobs((prev) => !prev)}
              >
                <span className="dashboard__matches-accordion-label">
                  Altre professioni ({otherJobs.length})
                </span>
                <span className="dashboard__matches-accordion-icon" aria-hidden="true" />
              </button>

              {showAllJobs && (
                <ul
                  id="external-cv-other-jobs"
                  className="dashboard__matches-list"
                  aria-label="Altre professioni"
                >
                  {otherJobs.map((job) => (
                    <li key={job.jobId}>
                      <JobMatchCard
                        job={job}
                        interactive={jobInteractive}
                        detailPath={jobDetailPath}
                        backTo={jobBackTo}
                        compact
                      />
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      )}

      {/* Sezioni estratte dal CV (Europass), nello stesso flusso: esperienza,
          istruzione, lingue, progetti, poi la sezione unificata delle
          certificazioni (Credly + CV), i riconoscimenti e le pubblicazioni.
          Le sezioni puramente derivate dal CV richiedono `parsedCv`; la sezione
          certificazioni compare anche a CV assente se ci sono Credly. Se il CV
          non è disponibile e non c'è alcuna certificazione, si mostra lo stato
          dell'estrazione. */}
      {cvLoading ? (
        <Loading>Caricamento del CV…</Loading>
      ) : cvError !== undefined ? (
        cvError
      ) : (
        <>
          {cvForSections !== null && (
            <>
              <EuropassWorkExperienceSection cv={cvForSections} {...editableSectionProps} />
              <EuropassEducationSection cv={cvForSections} {...editableSectionProps} />
              <EuropassLanguagesSection cv={cvForSections} {...editableSectionProps} />
              <EuropassProjectsSection cv={cvForSections} {...editableSectionProps} />
            </>
          )}

          {certificationEntries.length > 0 && (
            <section aria-labelledby={IDS.certifications} className="europass-cv__section">
              <div className="europass-cv__section-heading">
                <h2 id={IDS.certifications} className="europass-cv__section-title">
                  Certificazioni
                </h2>
              </div>

              {/* Prime CERTIFICATIONS_GRID_COUNT certificazioni sempre visibili
                  come card; le altre dietro l'accordion "Altre certificazioni"
                  in forma di tabella, come la sezione Skill. */}
              <ul className="certification-list certification-list--top">
                {topCertifications.map((entry, index) =>
                  renderCertificationEntry(entry, index),
                )}
              </ul>

              {hasOtherCertifications && (
                <>
                  <button
                    type="button"
                    className="dashboard__matches-accordion"
                    aria-expanded={showAllCertifications}
                    aria-controls="external-cv-other-certifications"
                    onClick={() => setShowAllCertifications((prev) => !prev)}
                  >
                    <span className="dashboard__matches-accordion-label">
                      Altre certificazioni ({otherCertifications.length})
                    </span>
                    <span
                      className="dashboard__matches-accordion-icon"
                      aria-hidden="true"
                    />
                  </button>

                  {showAllCertifications && (
                    <div
                      id="external-cv-other-certifications"
                      className="team-member-dashboard__other-skills"
                    >
                      <DataTable
                        rows={otherCertificationRows}
                        columns={certificationColumns}
                        rowKey={(row) => row.key}
                        emptyMessage="Nessuna altra certificazione."
                        defaultSort={{ key: 'issuedAt', direction: 'desc' }}
                        paginated={false}
                      />
                    </div>
                  )}
                </>
              )}
            </section>
          )}

          {cvForSections !== null ? (
            <>
              <EuropassAwardsSection cv={cvForSections} {...editableSectionProps} />
              <EuropassPublicationsSection cv={cvForSections} {...editableSectionProps} />
            </>
          ) : (
            // CV non disponibile e vista in sola lettura: mostra lo stato
            // dell'estrazione solo se non c'è già altro contenuto
            // (certificazioni) a occupare la vista. In Editable_Mode
            // `cvForSections` non è mai `null`, quindi questo ramo non si attiva.
            certifications.length === 0 &&
            cvOnlyCertifications.length === 0 && (
              <EmptyState role="status">
                {PARSED_CV_STATUS_MESSAGE[parsedCvStatus]}
              </EmptyState>
            )
          )}
        </>
      )}

      {/* Dettaglio del calcolo dello Score della skill selezionata, aperto dal
          badge Score cliccabile nella tabella "Skill valutate". Montato solo nel
          flusso interno autenticato (quando `scoringConfig` è presente): la vista
          pubblica non passa la config, quindi lo Score lì non è cliccabile. Il
          modale usa `createPortal`, perciò la posizione nel JSX non influisce sul
          layout. */}
      {scoringConfig !== undefined && scoreDetailRow !== null && (
        <ScoreDetailModal
          row={scoreDetailRow}
          config={scoringConfig}
          onClose={() => setScoreDetailRow(null)}
        />
      )}

      {/* Schemino con tutti i Livelli per fonte della skill selezionata, aperto
          dal contatore accanto al badge del Livello nella tabella "Skill
          valutate". Disponibile in tutti i flussi (non dipende da
          `scoringConfig`): compare solo quando la skill ha più di una Fonte di
          Livello. Usa `createPortal`, perciò la posizione nel JSX è ininfluente. */}
      {levelSourcesRow !== null && (
        <LevelSourcesModal
          row={levelSourcesRow}
          onClose={() => setLevelSourcesRow(null)}
        />
      )}
    </section>
  );
}

export default ExternalCvProfileView;
